import React, { useState } from 'react';
import { supabase } from '../../services/supabase.ts';

interface GenerateBillsModalProps {
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const GenerateBillsModal: React.FC<GenerateBillsModalProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return today.getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [notification, setNotification] = useState<string | null>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const generateBills = async (targetMonth: number, targetYear: number) => {
    // Get all active tenants
    const { data: tenants, error: tenantsError } = await supabase
      .from('tenants')
      .select('id, property_id, monthly_rent, payment_due_date')
      .eq('status', 'Active');

    if (tenantsError) throw tenantsError;

    // Create the first day of the month for bill date
    const monthStart = new Date(targetYear, targetMonth + 1);
    // Create the last day of the month for due date
    const monthEnd = new Date(targetYear, targetMonth + 1, 1);

    const { data: existingBills } = await supabase
      .from('bills')
      .select('tenant_id')
      .gte('month', monthStart.toISOString().split('T')[0])
      .lte('month', monthEnd.toISOString().split('T')[0]);

    const existingTenantIds = new Set(existingBills?.map(bill => bill.tenant_id) || []);

    // Generate bills for tenants who don't have one yet
    const newBills = tenants
      ?.filter(tenant => !existingTenantIds.has(tenant.id))
      .map(tenant => {
        let dueDate;
        console.log('tenantId', tenant.id);
        if (tenant.payment_due_date && tenant.payment_due_date > 0 && tenant.payment_due_date <= 31) {
          // Use tenant's payment_due_date
          const lastDayOfMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
          const adjustedDueDate = Math.min(tenant.payment_due_date, lastDayOfMonth);
          const customDueDate = new Date(targetYear, targetMonth+1, adjustedDueDate, 12, 0, 0);
          console.log('customDueDate', customDueDate);
          dueDate = customDueDate.toISOString().split('T')[0];
        } else {
          // Fallback to the last day of the month
          dueDate = monthEnd.toISOString().split('T')[0];
        }
        console.log('Final due date', dueDate);
        return {
          tenant_id: tenant.id,
          property_id: tenant.property_id,
          amount: tenant.monthly_rent,
          month: monthStart.toISOString().split('T')[0], // First day of month
          due_date: dueDate,
          status: 'Unpaid',
          paid_amount: 0
        };
      });

    if (!newBills?.length) {
      throw new Error(`All bills for ${months[targetMonth]} ${targetYear} have already been generated.`);
    }

    const { error: insertError } = await supabase
      .from('bills')
      .insert(newBills);

    if (insertError) throw insertError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNotification(null);
    
    try {
      await generateBills(selectedMonth, selectedYear);
      await onSuccess();
      onClose();
    } catch (error) {
    //   console.error('Error generating bills:', error);
      setNotification(error.message || 'Failed to generate bills');
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '' || /^\d{0,4}$/.test(value)) {
        const yearNum = parseInt(value) || 0;
        setSelectedYear(yearNum);
      }
    };

  return (
    <div className="modal" onClick={onClose}>
      <div className="bill-form" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate Bills</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {notification && (
          <div className={`alert ${notification.includes('Successfully') ? 'alert-success' : 'alert-error'}`}>
            {notification}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group month-year-group">
            <label>Select Month and Year</label>
            <div className="month-year-inputs">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="month-select"
              >
                {months.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={selectedYear || ''}
                onChange={handleYearChange}
                className="year-input"
                maxLength={4}
                placeholder="YYYY"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Generating...' : 'Generate Bills'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateBillsModal;
